import React, { useEffect, useState } from 'react'
import styles from './App.module.css'
import './index.css'
import { Layout, Menu, Card, FloatButton } from 'antd'
import { Link, Route, Routes, useLocation } from 'react-router-dom'
import Footer from './components/Footer/Footer'
import { Loading } from './components/Loading'
import { HomePage } from './pages/Home/HomePage'
import { getCookies, resetCookies, setCookie } from './services/helpers/helpers'
import { AccountPage } from './pages/Account/AccountPage'
import { acceptedCookieName, language } from './services/helpers/constants'
import { CookieConsent } from './components/CookieConsent/CookieConsent'
import { isNil } from 'ramda'
import { ClinicalCookies, User } from './services/interfaces'
import { UserContext } from 'services/contexts/userContext'
import { CasesPage } from 'pages/Cases/CasesPage'
import { CasePage } from 'pages/Case/CasePage'
import { Unauthorized } from './components/Unauthorized'
import { SamplesPage } from './pages/Samples/SamplesPage'
import { SamplePage } from 'pages/Sample/SamplePage'
import { PoolsPage } from './pages/Pools/PoolsPage'
import {
  DatabaseOutlined,
  DotChartOutlined,
  ExperimentOutlined,
  PlusCircleOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import { ApplicationsPage } from './pages/Applications/ApplicationsPage'
import { ApplicationPage } from './pages/Application/ApplicationPage'
import { OrderFormPage } from './pages/OrderForm/OrderFormPage'
import { ContactUs } from 'components/ContactUs/ContactUs'
import { OrderServiceFailure } from 'pages/Error/OrderServiceFailure'
import { useOidcAuth } from 'hooks/useOidcAuth'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { Header } from 'components/Header/Header'
import { useContentful } from 'hooks/useContentful'
import { googleLogout } from 'react-oauth-google'
import { GoogleAuthWrapper } from 'components/GoogleAuthWrapper'
import { ContentfulLayout } from 'components/Contentful/ContentfulLayout'

const { Content, Sider } = Layout

export const App = () => {
  const [googleUser, setGoogleUser] = useState<User | null>(null)
  const [googleToken, setGoogleToken] = useState<string | null>(null)
  const [acceptedCookiesPolicy, setAcceptedCookiesPolicy] = useState<boolean>(true)
  const [isCookieDialogVisible, setIsCookieDialogVisible] = useState<boolean>(true)
  const location = useLocation()
  const { user, token, isAuthenticated, isLoading } = useOidcAuth()
  const { contentfulUrl, contentfulToken, isServiceDown, IS_HOSPITAL } = useFeatureFlags()
  const {
    pages,
    contacts,
    isContentfulLoading,
    isAuthLoading,
    setIsAuthLoading,
    renderPageContent,
  } = useContentful(contentfulUrl, contentfulToken, language)

  useEffect(() => {
    const cookies: ClinicalCookies = getCookies()
    setAcceptedCookiesPolicy(!isNil(cookies[acceptedCookieName]))
  }, [])

  const acceptCookies = () => {
    setAcceptedCookiesPolicy(true)
    setCookie(true, acceptedCookieName)
    setIsCookieDialogVisible(false)
  }
  const rejectCookies = () => {
    setAcceptedCookiesPolicy(false)
    setIsCookieDialogVisible(false)
    resetCookies()
  }

  const sideMenuItems = [
    {
      key: '/new-order',
      icon: <PlusCircleOutlined />,
      label: (
        <Link to={'/new-order'}>
          <span>New order</span>
        </Link>
      ),
    },
    {
      key: '/cases',
      icon: <DatabaseOutlined />,
      label: (
        <Link to={'/cases'}>
          <span>Cases</span>
        </Link>
      ),
    },
    {
      key: '/samples',
      icon: <ExperimentOutlined />,
      label: (
        <Link to={'/samples'}>
          <span>Samples</span>
        </Link>
      ),
    },
    {
      key: '/pools',
      icon: <DotChartOutlined />,
      label: (
        <Link to={'/pools'}>
          <span>Pools</span>
        </Link>
      ),
    },
    {
      key: '/account',
      icon: <SettingOutlined />,
      label: (
        <Link to={'/account'}>
          <span>Account</span>
        </Link>
      ),
    },
  ]

  const logout = () => {
    googleLogout()
    setGoogleToken(null)
    setGoogleUser(null)
  }

  const PrivateRoute = ({ element: Element, ...rest }) => {
    if (!IS_HOSPITAL) {
      if (!googleToken || !googleUser) {
        return <Unauthorized />
      }
    } else {
      if (!isAuthenticated) {
        return <Unauthorized />
      }
    }

    return <Element {...rest} />
  }

  return (
    <>
      <GoogleAuthWrapper>
        {isContentfulLoading ? (
          <Loading />
        ) : (
          <Layout style={{ minHeight: '100vh' }}>
            <UserContext.Provider
              value={{
                token: IS_HOSPITAL ? token : googleToken,
                user: IS_HOSPITAL ? user : googleUser,
                logout: IS_HOSPITAL
                  ? () => {
                      /* no-op */
                    }
                  : logout,
              }}
            >
              {!acceptedCookiesPolicy && (
                <CookieConsent
                  visible={isCookieDialogVisible}
                  accept={acceptCookies}
                  reject={rejectCookies}
                />
              )}
              <Header
                pages={pages}
                user={googleUser}
                setGoogleToken={setGoogleToken}
                setGoogleUser={setGoogleUser}
                googleLogout={logout}
                setIsAuthLoading={setIsAuthLoading}
              />
              <Layout>
                {(IS_HOSPITAL ? isLoading : isAuthLoading) ? (
                  <Loading />
                ) : (
                  <>
                    <>
                      {!isNil(IS_HOSPITAL ? user : googleUser) && (
                        <Sider collapsible={false} collapsed={false} className={styles.slider}>
                          <Menu
                            mode="inline"
                            defaultSelectedKeys={['1']}
                            defaultOpenKeys={['sub1']}
                            style={{ height: '100%', borderRight: 0, zIndex: 10 }}
                            selectedKeys={[location.pathname]}
                            items={sideMenuItems}
                          />
                        </Sider>
                      )}
                    </>
                    <Layout>
                      <Content style={{ padding: 30 }}>
                        <Routes>
                          <Route
                            path="/"
                            element={<HomePage user={user} googleUser={googleUser} />}
                          />
                          <Route
                            path="/callback"
                            element={<HomePage user={user} googleUser={googleUser} />}
                          />
                          <Route path="/contact-us" element={<ContactUs contacts={contacts} />} />

                          {pages?.map((page) => {
                            const routePath = page?.fields?.title?.route
                            const content = page?.fields?.content

                            if (!routePath || !content) return null

                            const { toc, content: pageContent } = renderPageContent(content)

                            return (
                              <Route
                                key={routePath}
                                path={routePath}
                                element={<ContentfulLayout toc={toc} content={pageContent} />}
                              />
                            )
                          })}

                          {(IS_HOSPITAL ? !isLoading : !isAuthLoading) && (
                            <>
                              <Route path="/cases" element={<PrivateRoute element={CasesPage} />} />
                              <Route
                                path="/cases/:caseId"
                                element={<PrivateRoute element={CasePage} />}
                              />
                              <Route
                                path="/account"
                                element={<PrivateRoute element={AccountPage} />}
                              />
                              <Route
                                path="/samples"
                                element={<PrivateRoute element={SamplesPage} />}
                              />
                              <Route
                                path="/samples/:sampleId"
                                element={<PrivateRoute element={SamplePage} />}
                              />
                              <Route path="/pools" element={<PrivateRoute element={PoolsPage} />} />
                              <Route
                                path="/new-order"
                                element={
                                  isServiceDown ? (
                                    <OrderServiceFailure />
                                  ) : (
                                    <PrivateRoute element={OrderFormPage} />
                                  )
                                }
                              />
                              <Route path="/applications" element={<ApplicationsPage />} />
                              <Route
                                path="/applications/:applicationId"
                                element={<ApplicationPage />}
                              />
                            </>
                          )}
                        </Routes>
                      </Content>
                      <Footer />
                    </Layout>
                  </>
                )}
              </Layout>
            </UserContext.Provider>
          </Layout>
        )}
      </GoogleAuthWrapper>
      <FloatButton.BackTop type="primary" />
    </>
  )
}
